import type { GigOrderAdminDetail, GigOrderAdminEvent, GigOrderAdminItem } from '@/models/GigWorker';

const recordedText = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null);
const reasonText = (value: unknown): string => recordedText(value) ?? 'Reason not recorded';
const workerText = (name: string | null, id: string | null): string => {
  const displayName = recordedText(name);
  const workerId = recordedText(id);
  return displayName && workerId ? `${displayName} (${workerId})` : (displayName ?? workerId ?? 'Worker not recorded');
};
const timeText = (value: string | null): string => {
  const timestamp = value ? new Date(value) : null;
  return timestamp && Number.isFinite(timestamp.getTime()) ? timestamp.toLocaleString() : 'Time not recorded';
};

export function GigItemReason({ item }: { item: GigOrderAdminItem }) {
  if (!item.issue) {
    const affected = item.internalQuantity > 0 || item.purchaseStatus === 'OUT_OF_STOCK';
    return <span className="text-muted">{affected ? 'Reason not recorded' : 'No item issue recorded'}</span>;
  }
  return (
    <div>
      <div className="text-muted">
        Item issue record · {item.issue.quantity} units · {workerText(item.issue.reportedByName, item.issue.reportedBy)}
        {' · '}
        {timeText(item.issue.createdAt)}
      </div>
      <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{reasonText(item.issue.reason)}</div>
    </div>
  );
}

export function GigOrderSkips({ detail }: { detail: GigOrderAdminDetail }) {
  return (
    <section aria-label="Whole-order skips" className="mb-3 small">
      <h6 className="small text-uppercase text-muted">Whole-order skips</h6>
      <p className="text-muted mb-2">Applies to the whole GIG order/batch. Not individually recorded item reasons.</p>
      {detail.orderSkips?.length ? (
        <ul className="mb-0 ps-3">
          {detail.orderSkips.map((skip, index) => (
            <li key={`${skip.workerId}-${index}`} className="mb-2">
              <div className="text-muted">
                {workerText(skip.workerName, skip.workerId)} · {timeText(skip.createdAt)}
              </div>
              <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{reasonText(skip.reason)}</div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted mb-0">
          {detail.skipCount > 0 || detail.orderSkips === undefined
            ? 'Skip details not recorded'
            : 'No whole-order skips recorded'}
        </p>
      )}
    </section>
  );
}

interface GigItemReasonHistoryProps {
  event: GigOrderAdminEvent;
  items: GigOrderAdminItem[];
}

/** Only explicit item-event records are rendered; old/malformed detail never supplies an inferred reason. */
export function GigItemReasonHistory({ event, items }: GigItemReasonHistoryProps) {
  const isHandoff = event.eventType === 'UNAVAILABLE_ITEMS_HANDED_OFF';
  if (!isHandoff && event.eventType !== 'ORDER_ISSUE_REPORTED') {
    return null;
  }
  const raw = event.detail?.[isHandoff ? 'items' : 'issues'];
  const records = Array.isArray(raw)
    ? raw.filter(
        (value): value is Record<string, unknown> =>
          value !== null && typeof value === 'object' && !Array.isArray(value),
      )
    : [];
  return (
    <details className="small mt-1">
      <summary>{isHandoff ? 'Historical unavailable-item handoff' : 'Historical item report'}</summary>
      {records.length === 0 ? (
        <div>Item details / reason not recorded</div>
      ) : (
        records.map((record, index) => {
          const itemId = recordedText(record['itemId']);
          const item = itemId ? items.find((candidate) => candidate.itemId === itemId) : undefined;
          const omsOrderRef = recordedText(record['omsOrderRef']) ?? item?.omsOrderRef;
          const identity = `${itemId ?? 'Item not recorded'} · ${omsOrderRef ? `OMS ${omsOrderRef}` : 'OMS not recorded'}`;
          return (
            <div key={`${itemId}-${index}`} className="mt-2">
              {item?.name && <div>{item.name}</div>}
              <div>{identity}</div>
              <div className="text-muted">
                {typeof record['quantity'] === 'number' &&
                Number.isSafeInteger(record['quantity']) &&
                record['quantity'] > 0
                  ? `${record['quantity']} units`
                  : 'Quantity not recorded'}
              </div>
              <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{reasonText(record['reason'])}</div>
            </div>
          );
        })
      )}
    </details>
  );
}
